<template>
  <payment-container v-model="_selected" v-bind="containerProps">
    <template #control>
      <div v-if="active" class="mt-4">
        <o-field-group>
          <text-field v-model="query" icon="search" :placeholder="$t('components.order.elements.payments.static.giftCardOther.search')" />
        </o-field-group>

        <div class="mt-3">
          <div v-if="!query" class="p-2 bg-gray-100 dark:bg-gray-800/50 rounded-md block w-full">
            <p class="text-sm opacity-70 text-center">
              {{ $t('components.order.elements.payments.static.giftCardOther.searchAll') }}
            </p>
          </div>

          <div v-if="query && !loading && !giftCards" class="p-2 bg-gray-100 dark:bg-gray-800/50 rounded-md block w-full">
            <p class="text-sm opacity-70 text-center">
              {{ $t('components.order.elements.payments.static.giftCardOther.noMatching') }}
            </p>
          </div>

          <div v-if="query && loading" class="p-2 bg-gray-100 dark:bg-gray-800/50 rounded-md block w-full">
            <p class="text-sm opacity-70 text-center">
              {{ $t('components.order.elements.payments.static.giftCardOther.searching') }}
            </p>
          </div>

          <template v-if="giftCards && !loading">
            <template v-for="giftCard in giftCards" :key="giftCard.idGiftCard">
              <div
                v-if="giftCard"

                class="w-full p-2 bg-gray-100 dark:bg-gray-800/50 rounded-md flex items-center justify-between mb-1 cursor-pointer"
                :class="{ 'bg-white shadow-sm border': option.data && option.data.idGiftCard === giftCard.idGiftCard }"
                @click="_optionData = giftCard"
              >
                <div>
                  <p v-if="giftCard.customer" class="text-sm opacity-80">
                    {{ giftCard.customer.displayName }}
                  </p>
                  <p class="text-xs opacity-70">
                    {{ giftCard.customNumber || giftCard.number }}
                  </p>
                </div>

                <div>
                  <o-chip v-if="giftCard.isExpired" variant="warning" size="sm" flat>
                    {{ $t('general.expired') }}
                  </o-chip>
                  <o-chip variant="success" size="sm" flat>
                    {{ giftCard.balanceLabel }}
                  </o-chip>
                </div>
              </div>
            </template>
          </template>
        </div>
      </div>
    </template>
  </payment-container>
</template>

<script setup lang="ts">
import debounce from 'lodash/debounce'

import PaymentContainer from '@/components/order/elements/payments/PaymentContainer.vue'
import { TextField } from '@/components/fields'

const { $api, $i18n, $notification } = useNuxtApp()

interface Props {
  order: any
  amount: string | number
  tip?: number | string | null
  option: any
  selected?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  tip: null,
  selected: null,
})

const emit = defineEmits<{
  (e: 'update:selected', value: string): void
  (e: 'update:optionData', value: any): void
}>()

const _selected = computed({
  get: () => props.selected,
  set: (value: string) => emit('update:selected', value),
})

const active = computed(() => props.option.guid === props.selected)

const query = ref('')
const giftCards = ref<any[] | null>(null)
const loading = ref(false)

const containerProps = computed(() => ({
  label: $i18n.t('general.giftCard'),
  info: $i18n.t('components.order.elements.payments.static.giftCardOther.searchAllCustomerGiftCards'),
  option: props.option,
}))

const _optionData = computed({
  get: () => props.option.data,
  set: (value: any) => emit('update:optionData', value),
})

const searchGiftCards = debounce(async () => {
  _optionData.value = null
  giftCards.value = null

  if (query.value) {
    loading.value = true
    try {
      const q = query.value
      const result = await $api.giftCard.search(q)
      giftCards.value = result.items
    } catch (e: any) {
      $notification.error({ errors: e?.errors })
    }
    loading.value = false
  }
}, 500)

watch(query, () => {
  searchGiftCards()
})

async function process() {
  const { amount, tip, option } = props
  const payment = option.create(amount, tip)
  const response = await $api.order.addPayment(String(props.order.idOrder), payment)
  return response.data
}

async function saturate() {
  const response = await $api.order.get(String(props.order.idOrder))
  return response.data
}

defineExpose({ process })
</script>
